/**
 * Report what anonymous visitors do, so the free-grade cap can be tuned on
 * evidence instead of taste.
 *
 * Added 2026-09-27. The cap lived entirely in localStorage, so there was no way
 * to know whether 5 grades is generous or stingy — whether people reach it
 * (good: they wanted more) or never get past two (the cap is irrelevant and the
 * problem is elsewhere).
 *
 * PRIVACY: `ag_visitor` is a random UUID this file generates. It is not a
 * person — no email, no IP, no fingerprint, nothing cross-site. Clearing site
 * data makes a visitor new, which is fine: this measures funnel shape.
 *
 * Every call is fire-and-forget and swallows its own errors. A counter must
 * never be able to break the page it is counting, and this one sits on the
 * first screen a cold visitor ever sees.
 */

const VISITOR_KEY = 'ag_visitor';

function visitorId() {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() || `v-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    // Private window or blocked storage: still report the event so volume is
    // right, just without a visitor to attribute it to.
    return null;
  }
}

function send(event, { symbol, surface } = {}) {
  try {
    const body = JSON.stringify({ event, visitor_id: visitorId(), symbol, surface });
    // keepalive so an event fired as the user navigates away still lands.
    fetch('/api/public/guest-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never throw into a render path.
  }
}

/** An anonymous visitor was served a grade. */
export function trackGuestGrade(symbol, surface) {
  send('grade', { symbol, surface });
}

/** An anonymous visitor hit the free limit and saw the signup wall. */
export function trackGuestCapHit(surface) {
  send('cap_hit', { surface });
}
